import { Button, Label, NativeSelect } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";

/**
 * Settings › Site setup's badge: the embed a brand puts on its own site.
 *
 * Its language is the embed's, independent of the interface language, so it
 * has a select of its own rather than following the panel.
 */
export function BadgeSetup({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const [embed, setEmbed] = useState<string>(locale);
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "badge", embed],
		queryFn: () => api.badge(entityId, embed),
		...read,
	});

	async function copy(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("badge.copied"));
		} catch {
			// Clipboard access can be refused; the snippet is selectable anyway.
		}
	}

	return (
		<div className="stack">
			<div className="field">
				<Label htmlFor="badge-locale">{t("badge.locale")}</Label>
				<NativeSelect id="badge-locale" value={embed} onChange={(event) => setEmbed(event.target.value)}>
					<option value="en">{t("locale.en")}</option>
					<option value="zh">{t("locale.zh")}</option>
				</NativeSelect>
			</div>
			<Async state={state}>
				{(badge) => (
					<div style={{ display: "flex", flexDirection: "column", gap: "var(--s-10)" }}>
						<section className="panel">
							{/* Preview through the API so the console does not depend on the public site being up. */}
							<img
								src={`/api/v1/public/badge/${encodeURIComponent(badge.token)}.svg?locale=${encodeURIComponent(embed)}`}
								alt={t("badge.preview")}
								height={28}
							/>
							<p className="faint" style={{ marginTop: "var(--s-7)" }}>
								{badge.backlink_ok === null
									? t("badge.backlinkUnchecked")
									: badge.backlink_ok
										? t("badge.backlinkOk")
										: t("badge.backlinkMissing")}
								{badge.backlink_checked_at ? ` · ${fmt.relative(locale, badge.backlink_checked_at)}` : ""}
							</p>
						</section>

						<Snippet
							label={t("badge.html")}
							value={badge.html}
							onCopy={() => copy(badge.html)}
							copyLabel={t("badge.copy")}
						/>
						<Snippet
							label={t("badge.markdown")}
							value={badge.markdown}
							onCopy={() => copy(badge.markdown)}
							copyLabel={t("badge.copy")}
						/>
					</div>
				)}
			</Async>
		</div>
	);
}

function Snippet({
	label,
	value,
	onCopy,
	copyLabel,
}: {
	label: string;
	value: string;
	onCopy: () => void;
	copyLabel: string;
}) {
	return (
		<section className="panel">
			<div className="row-actions" style={{ justifyContent: "space-between", marginBottom: "var(--s-6)" }}>
				<h2 style={{ margin: 0 }}>{label}</h2>
				<Button type="button" variant="default" onClick={onCopy}>
					{copyLabel}
				</Button>
			</div>
			<pre>{value}</pre>
		</section>
	);
}
